/* =========================================================================
   QUICK PRO — Quick Ship geometry arc behavior (<qp-qs-geometry>)
   -------------------------------------------------------------------------
   Renders nothing itself — sections/qp-qs-geometry.liquid server-renders
   the full poster/blueprint/table; this element adds:

   1. SELECTED-SIZE SYNC (read-only): the buy box's 'qp-qs:state' event is
      the change trigger, but its payload carries no size — so on each fire
      we re-read the selected size chip from the qp-qs-main DOM
      ([data-qs-option][data-option-index=sizeIndex].is-selected), the same
      read the size finder (<qp-qs-fit>) already performs for live stock.
      Matching column gets .is-active (2px lime underline + bold — the
      non-colour cue) and its visually-hidden "selected size" span; the
      blueprint dimension values + geometry-bound poster stats follow. NOTHING here
      writes back to the buy box.
      Degraded mode (no buy box / no Size option): column headers become a
      self-contained selector (role=button, Enter/Space).

   2. BLUEPRINT DRAW-ON: ink strokes draw via stroke-dashoffset, sequenced
      in DOM order over ~1.2s, triggered by a ONCE-ONLY IntersectionObserver;
      a single lime plot-head dot traces the active stroke (transient — the
      only other lime in the section is the selected-size underline).
      prefers-reduced-motion, Shopify design mode ([data-qs-geo-static] /
      Shopify.designMode), missing IO, or geometry APIs failing -> the
      drawing stays fully drawn and static (the CSS default; dash state is
      only ever applied here, immediately before animating).

   Property-name note: never `this.slot` (HTMLElement.prototype collision).
   ========================================================================= */
(function () {
  'use strict';

  var EVT = 'qp-qs:state';
  var DRAW_MS = 1200;

  if (customElements.get('qp-qs-geometry')) return;

  customElements.define('qp-qs-geometry', class extends HTMLElement {
    connectedCallback() {
      if (this._initDone) return;
      this._initDone = true;
      var self = this;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { self._setup(); });
      } else {
        this._setup();
      }
    }

    _setup() {
      var dataEl = this.querySelector('[data-qs-geo-data]');
      if (!dataEl) return;
      try { this.cfg = JSON.parse(dataEl.textContent); } catch (e) { return; }
      this.sizes = this.cfg.sizes || [];
      this.activeI = typeof this.cfg.active === 'number' ? this.cfg.active : 0;
      this.cols = Array.prototype.slice.call(this.querySelectorAll('[data-qs-geo-col]'));

      this._wireSync();
      this._wireDraw();
      this._wireLightbox();
      this._wireFold();
    }

    /* ------------------------------------------- collapsed rows (new PDP) */
    /* Inert unless the section rendered .qp-qs-geo__fold (pdp-bundle /
       pdp-upsell). Opens the matching <details> BEFORE any jump scrolls, so
       the shopper lands on revealed content. */
    _wireFold() {
      var fold = this.querySelector('.qp-qs-geo__fold');
      if (!fold) return;
      var geoRow = fold.querySelector('[data-qs-geo-row="geometry"]');
      var chartRow = fold.querySelector('[data-qs-geo-row="chart"]');
      /* row 03 (QUESTIONS) renders in the closing section, outside this element */
      var faqRow = document.getElementById('qp-faq');
      if (faqRow && faqRow.tagName !== 'DETAILS') faqRow = null;
      var self = this;

      function rowForHash(hash) {
        if (hash === '#qp-geometry' || hash === '#qp-geometry-blueprint') return geoRow;
        if (hash === '#qp-geometry-chart') return chartRow;
        if (hash === '#qp-faq') return faqRow;
        return null;
      }

      function reveal(row) {
        if (!row) return;
        if (!row.open) row.open = true;
        if (typeof row.scrollIntoView === 'function') {
          row.scrollIntoView({ block: 'start' });
        }
      }

      /* draw-on: inside a closed <details> the once-only observer never
         fires, so opening the geometry row starts the same routine */
      if (geoRow) {
        geoRow.addEventListener('toggle', function () {
          if (geoRow.open && typeof self._drawStart === 'function') self._drawStart();
        });
      }

      function onHash() {
        var row = rowForHash(window.location.hash);
        if (!row) return;
        if (!row.open) row.open = true;
        /* wait a frame so the opened panel has laid out before scrolling */
        if (typeof requestAnimationFrame === 'function') {
          requestAnimationFrame(function () { reveal(row); });
        } else {
          reveal(row);
        }
      }

      window.addEventListener('hashchange', onHash);

      /* links outside this section (buy box GEOMETRY link, jump row) */
      document.addEventListener('click', function (e) {
        var t = e.target;
        var a = t && t.closest ? t.closest('a[href="#qp-geometry"]') : null;
        if (!a || !geoRow || e.defaultPrevented) return;
        if (e.button && e.button !== 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        /* the size finder's GEOMETRY pointer: close the modal first */
        if (a.hasAttribute('data-qs-fit-geo')) {
          var overlay = a.closest('[data-qs-fit-overlay]');
          var closeBtn = overlay ? overlay.querySelector('[data-qs-fit-close]') : null;
          if (closeBtn) closeBtn.click();
        }
        geoRow.open = true;
        if (window.location.hash !== '#qp-geometry' && window.history && window.history.pushState) {
          try { window.history.pushState(null, '', '#qp-geometry'); } catch (err) { /* ignore */ }
        }
        reveal(geoRow);
      });

      onHash(); /* on load */
    }

    /* ------------------------------------------------ selected-size sync */
    _wireSync() {
      /* build pages (PDP redesign) host the buy box in <qp-build-main>;
         its frame chips carry the same data-qs-option grammar */
      this.buyBox = document.querySelector('qp-build-main') || document.querySelector('qp-qs-main');
      var usable = !!(this.buyBox && this.cfg.sizeIndex >= 0 &&
        this.buyBox.querySelector('[data-qs-option][data-option-index="' + this.cfg.sizeIndex + '"]'));
      var self = this;

      if (usable) {
        document.addEventListener(EVT, function () { self._readBuyBox(); });
        /* the buy box may have rendered before we attached */
        this._readBuyBox();
        return;
      }

      /* Degraded: self-contained column selector on the headers. */
      this.cols.forEach(function (th, i) {
        th.classList.add('is-clickable');
        th.setAttribute('role', 'button');
        th.setAttribute('tabindex', '0');
        th.addEventListener('click', function () { self._setActive(i); });
        th.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            self._setActive(i);
          }
        });
      });
    }

    _readBuyBox() {
      var chip = this.buyBox.querySelector(
        '[data-qs-option][data-option-index="' + this.cfg.sizeIndex + '"].is-selected'
      );
      if (!chip) return;
      var i = this._matchIndex(chip.getAttribute('data-value'));
      if (i >= 0) this._setActive(i);
    }

    _matchIndex(raw) {
      if (raw == null) return -1;
      var v = String(raw).trim().toUpperCase();
      if (!v) return -1;
      for (var i = 0; i < this.sizes.length; i++) {
        var s = this.sizes[i];
        var key = (s.key || '').trim().toUpperCase();
        var label = (s.label || '').trim().toUpperCase();
        if (v === key || v === label) return i;
        /* label tokens: "505/M" matches chip values "505" or "M" */
        var parts = label.split('/');
        for (var j = 0; j < parts.length; j++) {
          if (parts[j].trim() === v) return i;
        }
      }
      return -1;
    }

    _setActive(i) {
      if (i === this.activeI || !this.sizes[i]) return;
      this.activeI = i;
      var s = this.sizes[i];

      this.cols.forEach(function (th, k) {
        var on = k === i;
        th.classList.toggle('is-active', on);
        if (th.getAttribute('role') === 'button') th.setAttribute('aria-pressed', on ? 'true' : 'false');
        var sr = th.querySelector('[data-qs-geo-sr]');
        if (sr) sr.hidden = !on;
      });

      /* blueprint dimension values */
      this._setVal('reach', s.reach);
      this._setVal('stack', s.stack);
      this._setVal('wheelbase', s.wheelbase);
      this._setVal('head', s.head);
      this._setVal('seat', s.seat);

      /* poster stats bound to geometry fields (+ size tokens in labels and
         canvas header). data-qs-geo-stat names mirror the JSON blob keys;
         head strips the degree sign (its unit renders separately). */
      var deg = /°/g;
      var root = this;
      ['head', 'wheelbase', 'stack', 'reach'].forEach(function (name) {
        root.querySelectorAll('[data-qs-geo-stat="' + name + '"]').forEach(function (el) {
          var v = s[name] == null ? '' : String(s[name]);
          if (name === 'head') v = v.replace(deg, '').trim();
          el.textContent = v;
        });
      });
      this.querySelectorAll('[data-qs-geo-size-token]').forEach(function (el) {
        el.textContent = s.label || '';
      });
    }

    _setVal(name, value) {
      this.querySelectorAll('[data-qs-geo-val="' + name + '"]').forEach(function (el) {
        el.textContent = value == null ? '' : value;
      });
    }

    /* --------------------------------------------------- blueprint draw */
    _wireDraw() {
      var svg = this.querySelector('[data-qs-geo-svg]');
      var canvas = this.querySelector('[data-qs-geo-canvas]');
      if (!svg || !canvas) return;

      var staticMode =
        this.hasAttribute('data-qs-geo-static') ||
        (window.Shopify && window.Shopify.designMode) ||
        (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) ||
        typeof IntersectionObserver === 'undefined';
      if (staticMode) return; /* fully drawn by CSS default — no dot */

      var strokes = Array.prototype.slice.call(svg.querySelectorAll('[data-qs-geo-stroke]'));
      if (!strokes.length) return;

      var lens = [];
      var total = 0;
      try {
        strokes.forEach(function (p) {
          var L = p.getTotalLength();
          lens.push(L);
          total += L;
        });
      } catch (e) { return; /* geometry API unavailable -> stay static */ }
      if (!(total > 0)) return;

      /* arm: hide every stroke (only ever done when we WILL animate) */
      strokes.forEach(function (p, i) {
        p.style.strokeDasharray = lens[i] + ' ' + lens[i];
        p.style.strokeDashoffset = lens[i];
      });

      var dot = svg.querySelector('[data-qs-geo-dot]');
      var self = this;
      var fired = false;
      var io = new IntersectionObserver(function (entries) {
        for (var k = 0; k < entries.length; k++) {
          if (entries[k].isIntersecting) {
            start();
            break;
          }
        }
      }, { threshold: 0.3 });
      function start() {
        if (fired) return;
        fired = true; /* once only */
        io.disconnect();
        self._animate(strokes, lens, total, dot);
      }
      /* the collapsed-row toggle calls this when the panel opens */
      this._drawStart = start;
      io.observe(canvas);
    }

    _animate(strokes, lens, total, dot) {
      var start = null;
      if (dot) dot.style.visibility = 'visible';

      function frame(ts) {
        if (start === null) start = ts;
        var t = Math.min((ts - start) / DRAW_MS, 1);
        var dist = t * total;

        var acc = 0;
        for (var i = 0; i < strokes.length; i++) {
          var L = lens[i];
          if (dist >= acc + L) {
            strokes[i].style.strokeDashoffset = 0;
          } else if (dist > acc) {
            var local = dist - acc;
            strokes[i].style.strokeDashoffset = L - local;
            if (dot) {
              try {
                var pt = strokes[i].getPointAtLength(local);
                dot.setAttribute('cx', pt.x);
                dot.setAttribute('cy', pt.y);
              } catch (e) { /* keep drawing without the dot */ }
            }
          } else {
            strokes[i].style.strokeDashoffset = L;
          }
          acc += L;
        }

        if (t < 1) {
          requestAnimationFrame(frame);
        } else {
          strokes.forEach(function (p) {
            p.style.strokeDashoffset = 0;
          });
          if (dot) dot.style.visibility = 'hidden'; /* transient — gone when done */
        }
      }
      requestAnimationFrame(frame);
    }

    /* ------------------------------------------ raster diagram lightbox */
    /* No-op when no diagram image is set (trigger/lightbox absent from the
       DOM in that mode) — the animated blueprint path above is untouched. */
    _wireLightbox() {
      var trigger = this.querySelector('[data-qs-geo-zoom-trigger]');
      var lightbox = this.querySelector('[data-qs-geo-lightbox]');
      if (!trigger || !lightbox) return;

      var closeBtn = lightbox.querySelector('[data-qs-geo-lightbox-close]');
      var backdrop = lightbox.querySelector('[data-qs-geo-lightbox-backdrop]');
      var FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      var lastTrigger = null;

      function getFocusable() {
        return Array.prototype.slice.call(lightbox.querySelectorAll(FOCUSABLE));
      }

      function onKeydown(e) {
        if (e.key === 'Escape') {
          e.preventDefault();
          close();
          return;
        }
        if (e.key === 'Tab') {
          var focusable = getFocusable();
          if (!focusable.length) return;
          var first = focusable[0];
          var last = focusable[focusable.length - 1];
          if (e.shiftKey) {
            if (document.activeElement === first) { e.preventDefault(); last.focus(); }
          } else {
            if (document.activeElement === last) { e.preventDefault(); first.focus(); }
          }
        }
      }

      function open() {
        lastTrigger = document.activeElement;
        lightbox.hidden = false;
        document.addEventListener('keydown', onKeydown);
        var focusable = getFocusable();
        if (focusable.length) focusable[0].focus();
      }

      function close() {
        lightbox.hidden = true;
        document.removeEventListener('keydown', onKeydown);
        if (lastTrigger && typeof lastTrigger.focus === 'function') lastTrigger.focus();
      }

      /* trigger is a <button> — native click fires on Enter/Space already */
      trigger.addEventListener('click', open);
      if (closeBtn) closeBtn.addEventListener('click', close);
      if (backdrop) backdrop.addEventListener('click', close);
    }
  });
})();
